/**
 * OmniLeagues — Family Directory ▸ Directory tab.
 *
 * Condensed table: family · father (name/phone/email) · mother (name/phone/email)
 * · address · children (+ team level that season). Reads OL Mongo directly via
 * `/api/exports/family-directory/{orgId}`. Season picker lists ONLY seasons that
 * have data. Search is client-side, debounced, focus-stable (does NOT refetch),
 * case-insensitive substring across family name, parents, emails/phones, address
 * and child names/team levels — within the selected season (or All). (mig_0124)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, Users } from 'lucide-react';
import { SearchBox, useDebounced, matchesAny } from './familyDirectoryShared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FamilyDirectoryPanel({ orgId, isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [seasonFilter, setSeasonFilter] = useState('');
  const seasonInit = useRef(false);
  const q = useDebounced(search, 250);

  const fetchData = useCallback(async () => {
    if (!orgId) return;
    try {
      const params = new URLSearchParams();
      if (sportFilter) params.set('sport_id', sportFilter);
      if (seasonFilter) params.set('season', seasonFilter);
      const { data } = await axios.get(`${API_URL}/api/exports/family-directory/${orgId}?${params}`);
      setData(data);
      if (!seasonInit.current) {
        seasonInit.current = true;
        const opts = data.season_options || [];
        if (data.current_season && opts.includes(data.current_season)) setSeasonFilter(data.current_season);
      }
    } catch (err) {
      console.error('Failed to load family directory:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, sportFilter, seasonFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.families.filter(f =>
      matchesAny(q, [
        f.family_name, f.father, f.mother, f.dad_email, f.mom_email,
        f.dad_cell, f.mom_cell, f.address,
        ...(f.children || []).map(c => `${c.name || ''} ${c.team || ''}`),
      ])
    );
  }, [data, q]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  if (!data) return null;

  const cardCls = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const rowHover = isDark ? 'hover:bg-slate-700/30' : 'hover:bg-amber-50/40';
  const border = isDark ? 'border-slate-700/40' : 'border-slate-200/60';
  const thBase = `text-left py-2 px-2 text-[11px] font-semibold uppercase tracking-wide ${muted}`;

  const parentCell = (name, phone, email) => (
    <div className="space-y-0.5 leading-tight">
      {name && <div className={text}>{name}</div>}
      {phone && <div className={muted}>{phone}</div>}
      {email && <div className={`${muted} truncate max-w-[200px]`}>{email}</div>}
      {!name && !phone && !email && <span className={muted}>—</span>}
    </div>
  );

  return (
    <div className="space-y-3" data-testid="family-directory-panel">
      <Card className={cardCls}>
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Users className={`h-4 w-4 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
            <span className={`text-sm font-semibold ${text}`}>{filtered.length} Families</span>
            <span className={`text-sm ${muted}`}>· {data.player_count} Players</span>
          </div>
          <SearchBox
            value={search}
            onChange={setSearch}
            placeholder="Search families, parents, emails, or players…"
            isDark={isDark}
            testId="family-search"
          />
          <select
            value={seasonFilter}
            onChange={(e) => { setSeasonFilter(e.target.value); setLoading(true); }}
            className={`rounded-md px-3 py-2 text-sm border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300'}`}
            data-testid="family-season-filter"
          >
            <option value="">All Seasons</option>
            {(data.season_options || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={sportFilter}
            onChange={(e) => { setSportFilter(e.target.value); setLoading(true); }}
            className={`rounded-md px-3 py-2 text-sm border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300'}`}
            data-testid="family-sport-filter"
          >
            <option value="">All Sports</option>
            {(data.sport_options || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </CardContent>
      </Card>

      <Card className={cardCls}>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="family-directory-table">
              <thead>
                <tr className={`border-b ${border}`}>
                  <th className={thBase}>Family</th>
                  <th className={thBase}>Father</th>
                  <th className={thBase}>Mother</th>
                  <th className={thBase}>Address</th>
                  <th className={thBase}>Children</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className={`py-6 text-center italic ${muted}`}>
                      No families match this filter.
                    </td>
                  </tr>
                )}
                {filtered.map((f, idx) => (
                  <tr
                    key={idx}
                    className={`border-b ${border} ${rowHover}`}
                    data-testid={`family-row-${idx}`}
                  >
                    <td className={`py-2 px-2 align-top font-semibold ${text}`}>{f.family_name}</td>
                    <td className="py-2 px-2 align-top">{parentCell(f.father, f.dad_cell, f.dad_email)}</td>
                    <td className="py-2 px-2 align-top">{parentCell(f.mother, f.mom_cell, f.mom_email)}</td>
                    <td className={`py-2 px-2 align-top ${muted}`}>
                      <span className="truncate inline-block max-w-[240px]">{f.address || '—'}</span>
                    </td>
                    <td className="py-2 px-2 align-top">
                      <div className="flex flex-wrap gap-1.5">
                        {(f.children || []).map((c, ci) => (
                          <Badge
                            key={ci}
                            variant="outline"
                            className={`text-[10px] ${isDark ? 'border-slate-600 text-slate-300' : 'border-slate-300 text-slate-600'}`}
                            title={`${c.name}${c.team ? ` · ${c.team}` : ''}${c.season ? ` · ${c.season}` : ''}${c.birthday ? ` · DOB ${c.birthday}` : ''}`}
                          >
                            {c.name}{c.team ? ` · ${c.team}` : ''}
                          </Badge>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
